// src/pages/LectureHowToUsePage.js
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageContainer from '../components/Layout/PageContainer';
import SeoHead from '../components/SeoHead';
import { useLanguage } from '../i18n/LanguageContext';
import LectureFaqMenu from '../components/LectureFaqMenu';
import '../styles/lectureUpload.css';

const T = {
  en: {
    headTitle: 'How to Use STT Lecture Transcription | Quick Start',
    headDesc:
      'Learn how to use the lecture STT service: record with the Chrome extension or use your own audio, upload, sign in, and check transcript & summary results on the Status page.',
    pageTitle: 'How to Use STT Lecture Transcription',
    intro: (
      <>
        Welcome to <strong>STT Lecture Transcription</strong> — a simple way to turn
        your lecture recordings into searchable transcripts and optional AI summaries.
        Just <strong>record</strong>, <strong>upload</strong>, and{' '}
        <strong>check your transcript on the Status page</strong>.
      </>
    ),

    // FAQ menu
    faqMenuTitle: 'STT FAQ Menu',
    faqMenuHowToUse: 'How to Use',
    faqMenuTips: 'Recording & Upload Tips',
    faqMenuLanguage: 'Supported Languages',
    faqMenuPrivacy: 'Privacy & Data',
    faqMenuAll: 'View all STT FAQs',

    // Steps
    s1: '1) Step 1 — Prepare your recording',
    s1a: (
      <>
        <strong>Option A — Use our Chrome extension</strong>: on the STT main page or{' '}
        <Link to="/lecture/upload">Upload</Link> page, click{' '}
        <strong>Open Recorder</strong> to launch the recorder. Then choose{' '}
        <strong>microphone recording</strong> or <strong>tab audio recording</strong>, and
        start recording your lecture or online class (e.g. Zoom, Google Meet, LMS playback).
      </>
    ),
    s1b: (
      <>
        <strong>Option B — Use your own audio file</strong>: you can also upload
        existing recordings from your phone, Zoom download, or other tools you use to
        record classes or meetings.
      </>
    ),
    s1c: (
      <>
        Supported formats: <code>.webm</code>, <code>.m4a</code>, <code>.mp3</code>,{' '}
        <code>.wav</code>.
      </>
    ),

    s2: '2) Step 2 — Sign in and open the Upload page',
    s2a: (
      <>
        Go to the <strong>Upload</strong> page (top menu or{' '}
        <Link to="/lecture/upload">this link</Link>).
      </>
    ),
    s2b: (
      <>
        If you are not signed in, click <strong>Sign in with Google</strong>. We use
        your account so that your recordings and transcripts are saved under your
        account and only accessible to you.
      </>
    ),

    s3: '3) Step 3 — Upload your recording',
    s3a: (
      <>
        In <strong>Step 2 — Upload your recording</strong>, click{' '}
        <strong>Select File</strong> and choose your audio file.
      </>
    ),
    s3b: (
      <>
        Fill in <strong>Lecture Title</strong>, <strong>Subject</strong>, and optional{' '}
        <strong>Tags</strong> (for example: <em>IFRS, revenue</em>). This helps you
        find it later on the Status page.
      </>
    ),
    s3c: (
      <>
        Under <strong>Language</strong>, you can keep <strong>Auto</strong> detection
        or switch to <strong>Manual</strong> and pick a specific locale (e.g.{' '}
        <em>English – All</em>, Cantonese, Mandarin).
      </>
    ),
    s3d: (
      <>
        <strong>For large audio files</strong>, we recommend using a stable Wi-Fi
        connection to avoid upload interruptions or failures.
      </>
    ),

    s4: '4) Step 4 — Submit and check Status',
    s4a: (
      <>
        Click <strong>Generate</strong>. Your file will enter the processing queue.
        Larger files may take longer to upload and process.
      </>
    ),
    s4b: (
      <>
        Go to the <strong>Status</strong> page to check progress and download transcripts
        (and AI summaries, if selected) once they are ready.
      </>
    ),

    s5: 'Tips for a smooth experience',
    s5a: (
      <>
        <strong>Keep background noise low</strong> where possible, so the STT engine can
        recognise speech more clearly.
      </>
    ),
    s5b: (
      <>
        <strong>Avoid frequent language switching</strong> in the same recording when
        you can. Switching between languages many times may reduce accuracy.
      </>
    ),
    s5c: (
      <>
        <strong>Check permissions</strong>: if using the extension, make sure Chrome has
        microphone permission and that the correct tab is selected for tab recording.
      </>
    ),
    s5d: (
      <>
        <strong>Sign in on each device</strong>: you can view your transcripts on any
        device where you sign in with the same Google account.
      </>
    ),

    notes: 'Notes',
    n1: (
      <>
        Your recordings are stored under your account. Please do not upload any content
        that violates local laws or platform policies.
      </>
    ),
    n2: (
      <>
        Where possible, avoid including highly sensitive personal information in
        recordings.
      </>
    ),
    n3: (
      <>
        For <strong>supported languages</strong> and <strong>privacy details</strong>,
        see the main{' '}
        <Link to="/lecture/faq" className="faq-link">
          STT FAQ page
        </Link>
        .
      </>
    ),
  },

  // Traditional Chinese
  'zh-Hant': {
    headTitle: '如何使用 STT 語音轉筆記｜快速上手',
    headDesc:
      '學會如何使用 STT 語音轉筆記服務：使用 Chrome 擴充功能錄音或上載現有錄音、登入帳號、在狀態頁查看逐字稿與摘要結果。',
    pageTitle: '如何使用 STT 語音轉筆記',
    intro: (
      <>
        歡迎使用 <strong>STT 語音轉筆記</strong>！
        你可以先錄製課堂或會議的聲音，再上載到系統，由我們自動幫你產生
        <strong>逐字稿</strong>與（可選）<strong>重點摘要</strong>。
        只要先<strong>錄音</strong>、再<strong>上載</strong>，最後到
        <strong>狀態頁（Status）</strong>查看結果即可。
      </>
    ),

    // FAQ menu
    faqMenuTitle: 'STT 常見問題選單',
    faqMenuHowToUse: '如何使用',
    faqMenuTips: '錄音與上傳小貼士',
    faqMenuLanguage: '支援語言',
    faqMenuPrivacy: '隱私與資料安全',
    faqMenuAll: '查看全部 STT 常見問題',

    s1: '1）步驟一：準備你的錄音',
    s1a: (
      <>
        <strong>方式 A — 使用 Chrome 擴充功能錄音</strong>：
        在 STT 主頁或{' '}
        <Link to="/lecture/upload">上載頁面</Link> 點擊
        <strong>「Open Recorder」</strong> 開啟錄音器，然後選擇
        <strong>「麥克風錄音」</strong>或<strong>「分頁錄音」</strong>，
        開始錄製課堂或線上課程（例如 Zoom、Google Meet、LMS 課程影片）。
      </>
    ),
    s1b: (
      <>
        <strong>方式 B — 使用你已有的錄音檔</strong>：
        例如手機錄音、Zoom 下載檔等，也可以在課後直接上載使用。
      </>
    ),
    s1c: (
      <>
        支援格式：<code>.webm</code>、<code>.m4a</code>、<code>.mp3</code>、
        <code>.wav</code>。
      </>
    ),

    s2: '2）步驟二：登入並前往上傳頁面',
    s2a: (
      <>
        進入網站的 <strong>「上載 Upload」</strong>{' '}
        頁面（主選單或 <Link to="/lecture/upload">此連結</Link>）。
      </>
    ),
    s2b: (
      <>
        若尚未登入，請點擊 <strong>「使用 Google 登入」</strong>。
        我們會以你的帳號管理錄音與逐字稿，方便你日後查閱。
      </>
    ),

    s3: '3）步驟三：上載錄音檔',
    s3a: (
      <>
        在 <strong>「Step 2 — Upload your recording」</strong> 區塊中，
        點擊<strong>「Select File」</strong>，選擇你的錄音檔。
      </>
    ),
    s3b: (
      <>
        填寫 <strong>Lecture Title（課堂名稱）</strong>、
        <strong>Subject（科目）</strong>，以及可選的{' '}
        <strong>Tags（標籤）</strong>（例如：<em>IFRS, revenue</em>），
        方便你日後在狀態頁面搜尋與整理。
      </>
    ),
    s3c: (
      <>
        在 <strong>Language</strong> 區域，你可以保持{' '}
        <strong>Auto（自動偵測）</strong>，或改用{' '}
        <strong>Manual（手動）</strong>，選擇特定語言（例如{' '}
        <em>English – All</em>、廣東話、普通話）。
      </>
    ),
    s3d: (
      <>
        <strong>若錄音檔較大</strong>，建議使用穩定的 Wi-Fi 網絡進行上載，
        以避免中途中斷或上載失敗。
      </>
    ),

    s4: '4）步驟四：送出並在狀態頁查看結果',
    s4a: (
      <>
        點擊 <strong>Generate（產生）</strong>，系統會將你的檔案排入處理隊列。
        較長的錄音可能需要更久時間處理。
      </>
    ),
    s4b: (
      <>
        前往 <strong>狀態頁（Status）</strong>，即可查看處理進度，
        並在完成後下載逐字稿與（如有）摘要。
      </>
    ),

    s5: '使用小貼士',
    s5a: (
      <>
        <strong>盡量減少背景噪音</strong>，可以提升語音辨識的準確度。
      </>
    ),
    s5b: (
      <>
        <strong>建議一個錄音以一種語言為主</strong>；
        若在同一段錄音中頻繁切換語言，可能影響辨識準確度。
      </>
    ),
    s5c: (
      <>
        <strong>確認權限設定</strong>：
        使用擴充功能時，請確保 Chrome 已允許麥克風，
        並選擇正確的分頁進行錄音。
      </>
    ),
    s5d: (
      <>
        <strong>在不同裝置登入同一帳號</strong>，
        即可於多個裝置查看相同的逐字稿與紀錄。
      </>
    ),

    notes: '注意事項',
    n1: (
      <>
        錄音檔會以你的帳號儲存。請勿上載任何違反當地法律或平台政策的內容。
      </>
    ),
    n2: (
      <>
        盡量避免在錄音中加入高度敏感的個人資料或機密內容。
      </>
    ),
    n3: (
      <>
        關於 <strong>支援語言</strong> 與 <strong>資料隱私</strong>{' '}
        的詳細說明，請參考主{' '}
        <Link to="/lecture/faq" className="faq-link">
          STT 常見問題頁面
        </Link>
        。
      </>
    ),
  },

  // Simplified Chinese
  'zh-Hans': {
    headTitle: '如何使用 STT 语音转笔记｜快速上手',
    headDesc:
      '学会如何使用 STT 语音转笔记服务：使用 Chrome 扩展录音或上传现有录音、登录帐号，在状态页查看逐字稿与摘要结果。',
    pageTitle: '如何使用 STT 语音转笔记',
    intro: (
      <>
        欢迎使用 <strong>STT 语音转笔记</strong>！
        你可以先录制课堂或会议的声音，然后上传到系统，由我们自动帮你生成
        <strong>逐字稿</strong>与（可选）<strong>重点摘要</strong>。
        只需要先<strong>录音</strong>、再<strong>上传</strong>，最后到
        <strong>状态页（Status）</strong>查看结果即可。
      </>
    ),

    faqMenuTitle: 'STT 常见问题菜单',
    faqMenuHowToUse: '如何使用',
    faqMenuTips: '录音与上传小贴士',
    faqMenuLanguage: '支持语言',
    faqMenuPrivacy: '隐私与数据安全',
    faqMenuAll: '查看全部 STT 常见问题',

    s1: '1）步骤一：准备你的录音',
    s1a: (
      <>
        <strong>方式 A — 使用 Chrome 扩展录音</strong>：
        在 STT 主页或{' '}
        <Link to="/lecture/upload">上传页面</Link> 点击
        <strong>“Open Recorder”</strong> 打开录音器，然后选择
        <strong>“麦克风录音”</strong>或<strong>“标签页录音”</strong>，
        开始录制课堂或线上课程（如 Zoom、Google Meet、LMS 课程视频）。
      </>
    ),
    s1b: (
      <>
        <strong>方式 B — 使用你已有的录音文件</strong>：
        例如手机录音、Zoom 下载文件等，也可以在课后直接上传使用。
      </>
    ),
    s1c: (
      <>
        支持格式：<code>.webm</code>、<code>.m4a</code>、<code>.mp3</code>、
        <code>.wav</code>。
      </>
    ),

    s2: '2）步骤二：登录并前往上传页面',
    s2a: (
      <>
        进入网站的 <strong>“上传 Upload”</strong>{' '}
        页面（主菜单或 <Link to="/lecture/upload">此链接</Link>）。
      </>
    ),
    s2b: (
      <>
        若尚未登录，请点击 <strong>“使用 Google 登录”</strong>。
        我们会以你的帐号管理录音与逐字稿，方便你之后查看。
      </>
    ),

    s3: '3）步骤三：上传录音文件',
    s3a: (
      <>
        在 <strong>“Step 2 — Upload your recording”</strong> 区块中，
        点击<strong>“Select File”</strong>，选择你的录音文件。
      </>
    ),
    s3b: (
      <>
        填写 <strong>Lecture Title（课堂名称）</strong>、
        <strong>Subject（科目）</strong>，以及可选的{' '}
        <strong>Tags（标签）</strong>（例如：<em>IFRS, revenue</em>），
        方便你之后在状态页面查找。
      </>
    ),
    s3c: (
      <>
        在 <strong>Language</strong> 区域，你可以保持{' '}
        <strong>Auto（自动检测）</strong>，或改用 <strong>Manual（手动）</strong>，
        选择特定语言（例如 <em>English – All</em>、粤语、普通话）。
      </>
    ),
    s3d: (
      <>
        <strong>如果录音文件较大</strong>，建议使用稳定的 Wi-Fi 网络进行上传，
        以避免中途断线或上传失败。
      </>
    ),

    s4: '4）步骤四：提交并在状态页查看结果',
    s4a: (
      <>
        点击 <strong>Generate（生成）</strong>，系统会将你的文件排入处理队列。
        较长的录音可能需要更长时间处理。
      </>
    ),
    s4b: (
      <>
        前往 <strong>状态页（Status）</strong>，即可查看处理进度，
        并在完成后下载逐字稿与（如有）摘要。
      </>
    ),

    s5: '使用小贴士',
    s5a: (
      <>
        <strong>尽量减少背景噪音</strong>，可以提高语音识别的准确度。
      </>
    ),
    s5b: (
      <>
        <strong>建议一段录音以一种语言为主</strong>；
        在同一段录音里频繁切换语言可能会降低识别准确度。
      </>
    ),
    s5c: (
      <>
        <strong>确认权限设置</strong>：
        使用扩展时，请确保 Chrome 已允许麦克风，
        并选择正确的标签页进行录音。
      </>
    ),
    s5d: (
      <>
        <strong>在不同设备登录同一帐号</strong>，
        即可在多个设备查看相同的逐字稿与记录。
      </>
    ),

    notes: '注意事项',
    n1: (
      <>
        录音文件会以你的帐号保存。请不要上传任何违反当地法律或平台政策的内容。
      </>
    ),
    n2: (
      <>
        尽量避免在录音中加入高度敏感的个人信息或机密内容。
      </>
    ),
    n3: (
      <>
        关于 <strong>支持语言</strong> 与 <strong>数据隐私</strong>{' '}
        的详细说明，请参考主{' '}
        <Link to="/lecture/faq" className="faq-link">
          STT 常见问题页面
        </Link>
        。
      </>
    ),
  },
};


function detectUiLocale(navUploadLabel) {
  if (!navUploadLabel) return 'en';
  if (navUploadLabel === 'Upload') return 'en';
  if (navUploadLabel.includes('上传')) return 'zh-Hans';
  if (/[\u4e00-\u9fff]/.test(navUploadLabel)) return 'zh-Hant';
  return 'en';
}

const LectureHowToUsePage = () => {
  const { t } = useLanguage();

  const navUploadLabel = t ? t('navUpload') : 'Upload';
  const uiLocale = detectUiLocale(navUploadLabel);

  const strings = useMemo(() => T[uiLocale] || T.en, [uiLocale]);
  const isEn = uiLocale === 'en';
  const url = 'https://stt.aittsservice.online/lecture/faq/how-to-use';

  const breadcrumbs = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: isEn ? 'STT Lecture Service' : 'STT 語音轉筆記服務',
          item: 'https://stt.aittsservice.online/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: isEn ? 'FAQ' : '常見問題',
          item: 'https://stt.aittsservice.online/lecture/faq',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: strings.pageTitle,
          item: url,
        },
      ],
    },
  ];

  return (
    <PageContainer>
      <SeoHead
        title={strings.headTitle}
        description={strings.headDesc}
        url={url}
        structuredData={breadcrumbs}
      />

      {/* Hero */}
      <div className="card upload-hero">
      <div className="card-heading">
        <h1 className="card-title">{strings.pageTitle}</h1>
      </div>
      <p>{strings.intro}</p>
      <p style={{ fontSize: 12, marginTop: 4 }}>{strings.lastUpdated}</p>
      </div>

      {/* Main content with FAQ menu */}
      <div className="card upload-card upload-faq-card">
        <div className="upload-layout upload-layout--faq howto-grid">
          <LectureFaqMenu active="how-to-use" />

          {/* Right: How-to steps */}
          <main className="upload-main upload-main--faq howto-main">
            <section className="faq-section">
              <h2>{strings.s1}</h2>
              <ul>
                <li>{strings.s1a}</li>
                <li>{strings.s1b}</li>
                <li>{strings.s1c}</li>
              </ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s2}</h2>
              <ul>
                <li>{strings.s2a}</li>
                <li>{strings.s2b}</li>
              </ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s3}</h2>
              <ul>
                <li>{strings.s3a}</li>
                <li>{strings.s3b}</li>
                <li>{strings.s3c}</li>
                <li>{strings.s3d}</li>
              </ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s4}</h2>
              <ul>
                <li>{strings.s4a}</li>
                <li>{strings.s4b}</li>
              </ul>
            </section>

            <section className="faq-section">
              <h2>{strings.s5}</h2>
              <ul>
                <li>{strings.s5a}</li>
                <li>{strings.s5b}</li>
                <li>{strings.s5c}</li>
                <li>{strings.s5d}</li>
              </ul>
            </section>

            <section className="faq-section">
              <h2>{strings.notes}</h2>
              <ul>
                <li>{strings.n1}</li>
                <li>{strings.n2}</li>
                <li>{strings.n3}</li>
              </ul>
            </section>
          </main>
        </div>
      </div>
    </PageContainer>
  );
};

export default LectureHowToUsePage;
